import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateModule } from '@ngx-translate/core';

import { DkGalleryItem } from '../dk-image-gallery/dk-image-gallery.types';

@Component({
  selector: 'dk-image-viewer',
  imports: [TranslateModule, NgOptimizedImage, NgTemplateOutlet],
  templateUrl: './dk-image-viewer.component.html',
  styleUrl: './dk-image-viewer.component.less',
  encapsulation: ViewEncapsulation.None,
})
export class DkImageViewerComponent implements OnInit, AfterViewInit {
  private readonly dialogRef = inject(DynamicDialogRef, { optional: true });
  private readonly dialogConfig = inject(DynamicDialogConfig, { optional: true });

  @Input() items: DkGalleryItem[] = [];
  @Input() activeIndex = 0;
  @Input() plantationOffset = '+00:00';
  @Input() footerTemplate?: TemplateRef<{ $implicit: DkGalleryItem; index: number }>;
  private _hasMore = false;
  pendingEnd = false;

  @Input()
  set hasMore(value: boolean) {
    this._hasMore = value;
    if (!value) {
      this.pendingEnd = false;
    }
  }

  get hasMore(): boolean {
    return this._hasMore;
  }

  @Output() activeIndexChange = new EventEmitter<number>();
  @Output() closed = new EventEmitter<void>();
  @Output() reachedEnd = new EventEmitter<void>();
  @Output() reachedStart = new EventEmitter<void>();

  @ViewChild('viewerRoot') viewerRoot?: ElementRef<HTMLElement>;

  readonly brokenImageIds = new Set<string>();
  private opener?: HTMLElement | null;

  get currentItem(): DkGalleryItem | undefined {
    return this.items[this.activeIndex];
  }

  get nextDisabled(): boolean {
    return this.activeIndex >= this.items.length - 1 && (!this.hasMore || this.pendingEnd);
  }

  ngOnInit(): void {
    this.dialogConfig?.data?.attach?.(this);
  }

  ngAfterViewInit(): void {
    this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.viewerRoot?.nativeElement.focus();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goToPrevious();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goToNext();
    }
  }

  goToNext(): void {
    if (this.activeIndex >= this.items.length - 1) {
      if (!this.hasMore || this.pendingEnd) {
        return;
      }
      this.pendingEnd = true;
      this.reachedEnd.emit();
      return;
    }

    this.activeIndex += 1;
    this.activeIndexChange.emit(this.activeIndex);
  }

  goToPrevious(): void {
    if (this.activeIndex <= 0) {
      this.reachedStart.emit();
      return;
    }

    this.activeIndex -= 1;
    this.activeIndexChange.emit(this.activeIndex);
  }

  setItems(items: DkGalleryItem[]): void {
    const grew = items.length > this.items.length;
    this.items = items;
    if (grew) {
      this.pendingEnd = false;
    }
  }

  markImageAsBroken(id: string): void {
    this.brokenImageIds.add(id);
  }

  close(): void {
    this.closed.emit();
    this.dialogRef?.close();
    this.opener?.focus();
  }
}
